/**
 * 全局统一题目类型系统
 * 
 * 核心设计原则：
 * 1. title = 短题目名称（所有题型统一，如"二叉树遍历"）
 * 2. content = 完整富文本题干（所有题型统一）
 * 3. 图片统一为 ImageItem 对象数组，消除双数组索引同步 Bug
 * 4. 分题型扩展专属字段，公共字段通过 BaseQuestion 强制约束
 */

// ─── 图片统一结构 ───
export interface ImageItem {
  url: string;
  widthPercent: number; // 10-200，默认100
}

// ─── 选项统一结构 ───
export interface Option {
  id: string;
  label: string;
  text: string;
  isCorrect: boolean;
  images: ImageItem[]; // 统一图片结构（含宽度）
}

// ─── 题型枚举 ───
export type QuestionType =
  | 'single_choice'
  | 'multi_choice'
  | 'judge'
  | 'subjective'
  | 'python_coding'
  | 'cpp_coding'
  | 'scratch_coding'
  | 'graphics';

// ─── 难度枚举 ───
export type Difficulty = 'easy' | 'medium' | 'hard';

// ─── 全局公共字段（所有题型必带） ───
export interface BaseQuestion {
  id: string;
  title: string;        // 短题目名称（所有题型统一）
  content: string;      // 完整富文本题干（所有题型统一）
  type: QuestionType;
  score: number;
  difficulty: Difficulty;
  titleImages: ImageItem[];
  createTime: string;
  updateTime: string;
  categoryId: string;
  // 布局字段
  titleImageLayout?: string;
  imagePosition?: string;
  optionLayout?: string;
  // 答案解析（所有题型可选）
  explanation?: string;
}

// ─── 客观题扩展 ───
export interface ObjectiveQuestion extends BaseQuestion {
  type: 'single_choice' | 'multi_choice' | 'judge';
  options: Option[];
  correctAnswer: string; // 如 "A" 或 "A,B,C"
}

// ─── 主观题扩展 ───
export interface SubjectiveQuestion extends BaseQuestion {
  type: 'subjective';
  referenceAnswer?: string;
  scoringPoints?: string;
  referenceAnswerImages?: ImageItem[];
  scoringPointsImages?: ImageItem[];
  gradingMode?: 'ai' | 'manual';
}

// ─── Python 编程题扩展 ───
export interface PythonCodingQuestion extends BaseQuestion {
  type: 'python_coding';
  codeTemplate?: string;
  referenceAnswer?: string;
  scoringPoints?: string;
  testCases?: TestCase[];
  structuredScoring?: StructuredScoringConfig;
  gradingMode?: 'oj_ai' | 'manual';
  timeLimitMs?: number;
  memoryLimitMb?: number;
  difficulty: Difficulty;
  knowledgePointIds?: string[];
  commonMistakes?: string;
  pythonEditorFields?: PythonEditorFields;
}

// ─── C++ 编程题扩展 ───
export interface CppCodingQuestion extends BaseQuestion {
  type: 'cpp_coding';
  codeTemplate?: string;
  referenceAnswer?: string;
  demoCode?: string;
  scoringPoints?: string;
  testCases?: TestCase[];
  structuredScoring?: StructuredScoringConfig;
  gradingMode?: 'oj_ai' | 'manual';
  timeLimitMs?: number;
  memoryLimitMb?: number;
  difficulty: Difficulty;
  knowledgePointIds?: string[];
  commonMistakes?: string;
}

// ─── Scratch 编程题扩展 ───
export interface ScratchCodingQuestion extends BaseQuestion {
  type: 'scratch_coding';
  scratchSubtype?: 'algorithm' | 'operation' | 'creative';
  referenceSb3Url?: string;
  scratchAnswerItems?: ScratchAnswerItem[];
  scratchConfig?: ScratchConfig;
  scoringPoints?: string;
  referenceAnswer?: string;
  referenceAnswerImages?: ImageItem[];
  scoringPointsImages?: ImageItem[];
  gradingMode?: 'ai' | 'manual';
}

// ─── 图形化编程题扩展 ───
export interface GraphicsQuestion extends BaseQuestion {
  type: 'graphics';
  graphicsDifficulty?: Difficulty;
  graphicsCategory?: string;
  graphicsAnswerItems?: GraphicsAnswerItem[];
}

// ─── 联合类型 ───
export type Question =
  | ObjectiveQuestion
  | SubjectiveQuestion
  | PythonCodingQuestion
  | CppCodingQuestion
  | ScratchCodingQuestion
  | GraphicsQuestion;

// ─── 辅助类型 ───
export interface TestCase {
  id: string;
  input: string;
  expectedOutput: string;
  isHidden?: boolean;
  score?: number;
}

export interface StructuredScoringConfig {
  points: ScoringPoint[];
  commonMistakes: string;
}

export interface ScoringPoint {
  id: string;
  description: string;
  score: number;
}

export interface ScratchAnswerItem {
  id: string;
  order: number;
  name: string;
  placeholder: string;
  required: boolean;
  maxSizeMB: number;
  maxCount: number;
}

export interface ScratchConfig {
  maxFileSize?: number;
  allowedFileTypes?: string[];
}

export interface PythonEditorFields {
  starterCode?: string;
  referenceSolution?: string;
}

export interface GraphicsAnswerItem {
  id: string;
  order: number;
  name: string;
  placeholder: string;
  required: boolean;
  maxSizeMB: number;
  maxCount: number;
  maxBase64SizeKB?: number;
}

// ─── 兼容转换工具 ───

/**
 * 将旧格式双数组 (urls[] + widths[]) 转换为新格式 ImageItem[]
 * 用于 Mapper 层兼容旧数据库数据
 */
export function mergeImageArrays(urls: string[] | undefined, widths: number[] | undefined): ImageItem[] {
  if (!urls || urls.length === 0) return [];
  return urls.map((url, idx) => ({
    url,
    widthPercent: widths?.[idx] ?? 100,
  }));
}

/**
 * 将新格式 ImageItem[] 拆分为旧格式双数组
 * 用于向数据库写入时的兼容（过渡期）
 */
export function splitImageItems(items: ImageItem[] | undefined): { urls: string[]; widths: number[] } {
  if (!items || items.length === 0) return { urls: [], widths: [] };
  return {
    urls: items.map(item => item.url),
    widths: items.map(item => item.widthPercent),
  };
}

/**
 * 将旧格式 Option (含 image/images 双数组) 转换为新格式 Option (含 ImageItem[])
 */
export function convertLegacyOption(opt: any): Option {
  // 合并图片：优先 images[]，回退 image 单张
  const legacyUrls = opt.images && opt.images.length > 0
    ? opt.images
    : (opt.image ? [opt.image] : []);
  const legacyWidths = opt.imageWidths && opt.imageWidths.length > 0
    ? opt.imageWidths
    : (opt.imageWidth ? [opt.imageWidth] : []);

  return {
    id: opt.id || opt.key || '',
    label: opt.label || opt.id || '',
    text: opt.text || opt.content || '',
    isCorrect: opt.isCorrect || false,
    images: mergeImageArrays(legacyUrls, legacyWidths),
  };
}

/**
 * 判断是否为客观题
 */
export function isObjectiveQuestion(type: QuestionType | string): boolean {
  return type === 'single_choice' || type === 'multi_choice' || type === 'judge';
}

/**
 * 判断是否为编程题
 */
export function isCodingQuestion(type: QuestionType | string): boolean {
  return type === 'python_coding' || type === 'cpp_coding' || type === 'scratch_coding';
}
